import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import icClose from '@iconify/icons-ic/twotone-close';
import { DefaultSupervisionService } from '../../../../services/default-supervision.service';
import { UserProfileService } from '../../../../../pages/application/management/user-profile/user-profile.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import {
  DefaultSupervisionAssignableContact,
  DefaultSupervisionPropagationModeEnum,
  DefaultSupervisionScopeEnum,
  DefaultSupervisionTreeNode,
} from '../../../../models/default-supervision.model';

export interface DefaultSupervisionAssignModalData {
  mode: 'assign' | 'unassign';
  node: DefaultSupervisionTreeNode;
  scope: DefaultSupervisionScopeEnum;
  establishment?: { _id: string; label: string; technicalIdentifier?: string };
}

/**
 * Modale d'affectation / retrait d'un acteur « Supervision métier » par défaut sur un
 * nœud de l'arborescence, avec le choix de propagation aux Metrik existantes (PRED-3061) :
 * toutes les Metrik (y compris modifiées) ou uniquement celles définies par défaut.
 */
@Component({
  selector: 'default-supervision-assign-modal',
  templateUrl: './default-supervision-assign-modal.component.html',
  styleUrls: ['./default-supervision-assign-modal.component.scss'],
})
export class DefaultSupervisionAssignModalComponent implements OnInit {
  icClose = icClose;
  form: UntypedFormGroup;
  contacts$: Observable<DefaultSupervisionAssignableContact[]>;
  loading = false;

  DefaultSupervisionPropagationModeEnum = DefaultSupervisionPropagationModeEnum;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DefaultSupervisionAssignModalData,
    private dialogRef: MatDialogRef<DefaultSupervisionAssignModalComponent>,
    private defaultSupervisionService: DefaultSupervisionService,
    private userProfileService: UserProfileService,
    private snackbarService: SnackbarService,
  ) {}

  get isAssign(): boolean {
    return this.data.mode === 'assign';
  }

  get title(): string {
    return this.isAssign ? 'Modification du contact par défaut' : 'Retrait du contact par défaut';
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      contact: new UntypedFormControl(null, this.isAssign ? [Validators.required] : []),
      propagationMode: new UntypedFormControl(DefaultSupervisionPropagationModeEnum.ONLY_DEFAULT, [
        Validators.required,
      ]),
    });
    if (this.isAssign) {
      this.autocompleteContacts();
    }
  }

  autocompleteContacts(search = ''): void {
    this.contacts$ = this.userProfileService
      .findAll(null, null, 'fullName', 'asc', search, { isSupervision: true, isPredik: false })
      .pipe(map(result => result.data));
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const propagationMode: DefaultSupervisionPropagationModeEnum = this.form.value.propagationMode;

    const call$ = this.isAssign
      ? this.defaultSupervisionService.assign({
          scope: this.data.scope,
          establishment:
            this.data.scope === DefaultSupervisionScopeEnum.ESTABLISHMENT ? this.data.establishment : undefined,
          metrikType: this.data.node.metrikType,
          nodeType: this.data.node.nodeType,
          node: { _id: this.data.node._id, label: this.data.node.label },
          contactIntern: this.toContactRelation(this.form.value.contact),
          propagationMode,
        })
      : this.defaultSupervisionService.unassign({
          scope: this.data.scope,
          establishmentId:
            this.data.scope === DefaultSupervisionScopeEnum.ESTABLISHMENT ? this.data.establishment?._id : undefined,
          nodeType: this.data.node.nodeType,
          nodeId: this.data.node._id,
          propagationMode,
        });

    call$.subscribe({
      next: () => {
        this.snackbarService.success(
          this.isAssign ? 'Contact par défaut affecté avec succès' : 'Affectation retirée avec succès',
        );
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackbarService.danger('Une erreur est survenue lors de la mise à jour du contact par défaut');
        this.loading = false;
      },
    });
  }

  private toContactRelation(contact: DefaultSupervisionAssignableContact) {
    return {
      _id: contact._id,
      firstName: contact.firstName,
      lastName: contact.lastName,
    };
  }
}
